/**
 * `<Tooltip/>` — Radix-backed tooltip primitive.
 *
 * Used to surface dense-data context that would otherwise crowd the
 * surface (slot keys, assignment rows, status badges, capacity pill).
 * Per docs/launch/POC_UX_IMPROVEMENT_SPEC.md §3.8.
 *
 * Usage:
 *
 *   <Tooltip content="Row: full slot signature">
 *     <span>2025-01-12 · station-1 · AM</span>
 *   </Tooltip>
 *
 * The `content` prop accepts a string or arbitrary React node. The
 * trigger inherits its existing markup via `<Slot/>` so it doesn't
 * impose any wrapper element on the consumer.
 */

import * as TooltipPrimitive from "@radix-ui/react-tooltip";
import { type ReactNode, forwardRef } from "react";

import { cn } from "../../lib/cn";

/**
 * Mount once near the root of any tree that uses tooltips. The default
 * `delayDuration` is 250ms, a tick faster than Radix's default of 700ms
 * so dense-data hovers feel responsive without being twitchy.
 */
export const TooltipProvider = ({
  children,
  delayDuration = 250,
  skipDelayDuration = 100,
}: {
  children: ReactNode;
  delayDuration?: number;
  skipDelayDuration?: number;
}) => (
  <TooltipPrimitive.Provider delayDuration={delayDuration} skipDelayDuration={skipDelayDuration}>
    {children}
  </TooltipPrimitive.Provider>
);

interface TooltipProps {
  /** Text or node to render in the bubble. Pass `null` to disable. */
  content: ReactNode;
  /** The element to attach the tooltip to. Wrapped via Radix's Trigger. */
  children: ReactNode;
  /** Side preference. Defaults to `top`. */
  side?: "top" | "right" | "bottom" | "left";
  /** Distance from the trigger in px. */
  sideOffset?: number;
  /** When true, content stays as-is for screen-reader accessibility. */
  asChild?: boolean;
  /** Pass-through className for the bubble. */
  className?: string;
  /** #2352 — controlled open state (a "?" that also opens on click). */
  open?: boolean | undefined;
  onOpenChange?: ((open: boolean) => void) | undefined;
}

/**
 * `<Tooltip/>` — convenience wrapper combining Radix's Root + Trigger +
 * Content + Portal. For most callers this is enough. If you need
 * finer control (e.g. controlled open state) use the named exports
 * below directly.
 *
 * Embeds its own `<TooltipPrimitive.Provider>` so callers don't need
 * to remember to mount the app-wide provider. Radix providers nest
 * harmlessly, so the AppShell-level provider keeps governing global
 * `delayDuration` while standalone consumers (e.g. tests, isolated
 * components) still work without ceremony.
 */
export function Tooltip({
  content,
  children,
  side = "top",
  sideOffset = 6,
  asChild = true,
  className,
  open,
  onOpenChange,
}: TooltipProps) {
  if (content === null || content === undefined || content === "") {
    return <>{children}</>;
  }
  return (
    <TooltipPrimitive.Provider delayDuration={250} skipDelayDuration={100}>
      <TooltipPrimitive.Root
        {...(open !== undefined ? { open } : {})}
        {...(onOpenChange ? { onOpenChange } : {})}
      >
        <TooltipPrimitive.Trigger asChild={asChild}>{children}</TooltipPrimitive.Trigger>
        <TooltipPrimitive.Portal>
          <TooltipContent side={side} sideOffset={sideOffset} className={className}>
            {content}
          </TooltipContent>
        </TooltipPrimitive.Portal>
      </TooltipPrimitive.Root>
    </TooltipPrimitive.Provider>
  );
}

export const TooltipContent = forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Content>
>(({ className, sideOffset = 6, ...props }, ref) => (
  <TooltipPrimitive.Content
    ref={ref}
    sideOffset={sideOffset}
    className={cn(
      "z-50 max-w-xs rounded-md border border-border bg-bg-elevated px-2 py-1 text-xs text-fg shadow-sm",
      // #2172 — fades in moving 4 px away from the trigger, and fades out on
      // close (`motion-tooltip`, keyed on `data-side`).
      "motion-tooltip",
      className,
    )}
    {...props}
  />
));
TooltipContent.displayName = "TooltipContent";

/* Named exports so consumers can build their own composition when the
   convenience wrapper isn't enough. */
export const TooltipRoot = TooltipPrimitive.Root;
export const TooltipTrigger = TooltipPrimitive.Trigger;
export const TooltipPortal = TooltipPrimitive.Portal;
